<template>
  <v-layout>
    <v-dialog v-model="showAcceptBidDialog"
              lazy
              @input="v => v"
              max-width="500">
      <v-card class="pa-4">
        <v-layout column>
          <v-toolbar card>
            <v-toolbar-title>Accept Bid {{ bid.humanId }}</v-toolbar-title>
          </v-toolbar>
          <span class="title mt-2">{{ bid.truckingCompany.name }}</span>
          <span class="subheading mt-2">{{ bid.pickupTime | formatDate }}</span>
          <div>
            <span class="subheading mb-3 mt-2">${{ bid.amount.toFixed(2) }}</span>
            <v-chip v-if="loadCustomerName"
                    text-color="white"
                    :color="loadDispatchSpread < 0 ? 'red' : 'green'" small>
              ${{ loadDispatchSpread }}
            </v-chip>
          </div>
          <i>Offered loads: {{offeredLoads}}</i>
          <v-select
            :items="loadsOptions"
            v-model.number="acceptedLoads"
            :error-messages="errors.first('offeredLoads', 'modal')"
            data-vv-as="Offered Loads"
            data-vv-name="offeredLoads"
            data-vv-scope="modal"
            v-validate="`required|min_value:1|max_value:${offeredLoads}`"
            label="Accepted Loads">
          </v-select>

          <v-card-actions>
            <v-spacer />
            <v-btn color="accent"
                   :loading="loadingBtn"
                   :disabled="errors.any('modal')"
                   @click="acceptBidTrucks()">
              Accept
            </v-btn>
            <v-btn color="accent" flat
                   @click="closeAcceptBidDialog">
              Cancel
            </v-btn>
          </v-card-actions>
        </v-layout>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="showTimelineDialog"
      lazy
      :max-width="$vuetify.breakpoint.smAndDown ? '90%' : '600px'">
      <v-card class="pa-4">
        <bid-conversation :bid-id="bid.id"
                          :disable-form="false">

        </bid-conversation>

        <v-card-actions>
          <v-spacer/>
          <v-btn color="accent"
                 @click="closeTimelineDialog">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-card class="mr-3 mb-3">
      <v-container class="pt-1 pr-1">
        <v-toolbar class="pl-0 ml-0" card dense>
          <v-chip>
            <v-avatar class="primary">
              <v-icon color="white">local_shipping</v-icon>
            </v-avatar>
            <strong>{{ bid.numberOfLoads }}</strong>
          </v-chip>
          <v-toolbar-title class="pb-0 mb-0">
            ${{ bid.amount }}
          </v-toolbar-title>
          <v-spacer/>
          <v-subheader>{{ bid.humanId }}</v-subheader>
          <v-chip small
                  :color="bidChipColor">
            <v-icon v-if="isBidActive" color="white">local_offer</v-icon>
            <v-icon v-else-if="isBidAccepted" color="white">done</v-icon>
            <v-icon v-else disabled>highlight_off</v-icon>
          </v-chip>
        </v-toolbar>

        <v-list>
          <v-list-tile>
            <v-list-tile-action>
              <v-icon :color="isTCVerified ? 'green' : 'transparent'">verified_user</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{ bid.truckingCompany.name }}</v-list-tile-title>
              <v-list-tile-sub-title>${{ loadedDistanceBidRate }}/{{ siteDistanceMeasureUnitLabel }}
              </v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-avatar>
              <v-chip v-if="loadCustomerName"
                      text-color="white"
                      :color="loadDispatchSpread < 0 ? 'red' : 'green'" small>${{ loadDispatchSpread }}</v-chip>
            </v-list-tile-avatar>
          </v-list-tile>
          <v-list-tile>
            <v-list-tile-action>
              <v-icon color="primary">arrow_upward</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>
                <span :class="pickupTimeDifferenceInHours == 0 ? '' : 'font-weight-bold'">
                  {{bid.pickupTime | formatDate}}
                </span>
              </v-list-tile-title>
              <v-list-tile-sub-title v-if="pickupTimeDifferenceInHours != 0">
                <span class="font-italic">
                  {{ Math.abs(pickupTimeDifferenceInHours) }}
                  hour<span v-if="Math.abs(pickupTimeDifferenceInHours) != 1">s</span>
                  <strong v-if="pickupTimeDifferenceInHours < 0">Before</strong>
                  <strong v-if="pickupTimeDifferenceInHours > 0">After</strong>
                  requested pickup time</span>
              </v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-if="hasInsurancePolicy">
            <v-list-tile-action>
              <v-icon color="green lighten-2">check_circle</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Insurance Policy on file</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile v-else>
            <v-list-tile-action>
              <v-icon color="orange">warning</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Missing Insurance Policy</v-list-tile-title>
              <v-list-tile-sub-title><span class="font-italic">not uploaded to profile</span></v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-if="hasW9">
            <v-list-tile-action>
              <v-icon color="green lighten-2">check_circle</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>{{$t('company-profile.vendor-tax-document')}} on file</v-list-tile-title>
            </v-list-tile-content>
          </v-list-tile>
          <v-list-tile v-else>
            <v-list-tile-action>
              <v-icon color="orange">warning</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Missing {{$t('company-profile.vendor-tax-document')}}</v-list-tile-title>
              <v-list-tile-sub-title><span class="font-italic">not uploaded to profile</span></v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile @click="openBidTimelineDialog">
            <v-list-tile-action>
              <v-icon flat color="primary">message</v-icon>
            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Message Bidder</v-list-tile-title>
              <v-list-tile-sub-title>Resolve questions before accepting bid</v-list-tile-sub-title>
            </v-list-tile-content>
          </v-list-tile>

          <v-list-tile v-if="isBidActive">
            <v-list-tile-action>

            </v-list-tile-action>
            <v-list-tile-content>
              <v-list-tile-title>Accept Bid</v-list-tile-title>
              <v-list-tile-sub-title><span class="font-italic">and book loads</span></v-list-tile-sub-title>
            </v-list-tile-content>
            <v-list-tile-action>
              <v-btn color="accent"
                     @click="openAcceptBidDialog">
                <v-icon >add_circle</v-icon>
              </v-btn>
            </v-list-tile-action>
          </v-list-tile>
        </v-list>

      </v-container>
    </v-card>
  </v-layout>
</template>

<script>
import _ from 'lodash';
import * as types from '@/store/mutationTypes';
import { bidStatus } from '@/types';
import API from '@/api';
import BidConversation from '@/components/BidConversation';
import IconBase from '@/components/IconBase';
import IconHose from '@/components/icons/IconHose';
import IconWood from '@/components/icons/IconWood';
import IconCold from '@/components/icons/IconCold';
import * as Timestamps from '@/types/timestamp';

export default {
  name: 'VBidCard',
  components: {
    BidConversation,
    IconBase,
    IconHose,
    IconWood,
    IconCold,
  },
  props: {
    bid: {
      type: Object,
      required: true,
    },
    load: { // this is a planned load object
      type: Object,
      required: true,
    },
  },

  data: () => ({
    loadingBtn: false,
    showAcceptBidDialog: false,
    acceptedLoads: null,

    showTimelineDialog: false,
  }),
  computed: {
    _() {
      return _;
    },
    isTCVerified() {
      return this.bid.truckingCompany.verified;
    },
    pickupTimeDifferenceInHours() {
      const suggestion = this.bid.pickupTime;
      const requested = this.load.loadGroup.scheduledPickupTime;
      return Timestamps.hoursBetweenTimestamps(requested, suggestion);
    },
    loadCustomerName() {
      return _.get(this.load, 'customerCompany.name');
    },
    loadDispatchSpread() {
      if (this.loadCustomerName) {
        const pays = _.get(this.load, 'billableAmount');
        return pays - this.bid.amount;
      }
    },
    offeredLoads() {
      return this.bid.numberOfLoads;
    },
    loadsOptions() {
      return _.range(1, this.offeredLoads + 1).map((i) => {
        return { value: i, text: i };
      });
    },
    isBidActive() {
      const status = _.get(this.bid, 'status');
      return bidStatus.ACTIVE === status;
    },
    isBidAccepted() {
      const status = _.get(this.bid, 'status');
      return bidStatus.ACCEPTED === status;
    },
    loadedDistanceBidRate() {
      return this.distanceRateLabel(this.bid.amount, this.load.loadGroup.details.loadedMiles);
    },
    hasDocumentation() {
      return this.hasInsurancePolicy && this.hasW9;
    },
    hasInsurancePolicy() {
      const hauler = _.get(this.bid, 'truckingCompany', {});
      return hauler.insurancePolicyUrl;
    },
    hasW9(bid) {
      const hauler = _.get(bid, 'truckingCompany', {});
      return hauler.w9Url;
    },
    bidChipColor() {
      const status = _.get(this.bid, 'status');
      switch (status) {
        case bidStatus.ACTIVE: {
          return 'success';
        }
        case bidStatus.ACCEPTED: {
          return 'primary';
        }
        case bidStatus.CANCELLED_BY_BIDDER: {
          return '';
        }
        default:
      }
      return '';
    },
  },
  methods: {
    openAcceptBidDialog() {
      this.acceptedLoads = null;
      this.showAcceptBidDialog = true;
    },
    closeAcceptBidDialog() {
      this.acceptedLoads = null;
      this.showAcceptBidDialog = false;
    },
    async acceptBidTrucks() {
      const count = this.acceptedLoads;

      try {
        this.loadingBtn = true;

        const valid = await this.$validator.validateAll('modal');
        if (valid) {
          window.__insp.push(['tagSession', 'accept_bid']);
          await API.bid.acceptBid({ bidId: this.bid.humanId, numberOfLoads: count });
          this.$store.dispatch('applications/setSuccessNotification', {
            message: 'Bid Accepted',
          });
          this.$emit('refresh');
          this.closeAcceptBidDialog();
        }
      } catch (error) {
        window.__insp.push(['tagSession', 'accept_bid_error']);
        this.$store.dispatch('applications/setErrorNotification', {
          message: error.message,
        });
      } finally {
        this.loadingBtn = false;
      }
    },
    openBidTimelineDialog() {
      this.showTimelineDialog = true;
    },
    closeTimelineDialog() {
      this.showTimelineDialog = false;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/pages/Companies/Loads/components/BidCard.vue